import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type {
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import {
  DisruptionRebookPlanResponseEnum,
  type DisruptionRefundPlanRequest,
  type RebookPlanSuccess,
  type TripSlice,
} from "@b2bportal/air-disruption-api";

import {
  DisruptionExerciseInformationCard,
  DisruptionExerciseMoreInfo,
  DisruptionExerciseMoreInfoType,
  DisruptionExercisePage,
  DisruptionExercisePageContent,
  DisruptionExercisePageWidth,
  DisruptionExerciseProgress,
  DisruptionExerciseSummaryCard,
  disruptionRebookPlan,
  resetDisruptionExerciseState,
  setDisruptionExerciseProgress,
} from "@b2bportal/core-disruption";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  type AppDispatch,
  CoreDisruptionComponent,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { unwrapResult } from "@reduxjs/toolkit";
import defaultStyles from "./DisruptionExerciseRebookOnboardingPage.module.scss";

export interface DisruptionExerciseRebookOnboardingPageProps {
  disruptionProductName: string;
  disruptionProductLogo: string;
  disruptedFlight: BookedFlightItineraryWithDepartureTime;
  rebookingLimit?: string;
  contractId?: string;
  bookingReturn: boolean;
  termsLink: string;
  displayExternalLinkAsModal: boolean;
  delayHours: string;
  hoursString: string;
  onFlightSelectContinue: (props: {
    origin: string;
    destination: string;
    departure: string;
    tripSlice?: TripSlice;
  }) => void;
  onContactSupport: () => void;
  handleRefundClick: () => void;
  setRebookPlanResponse: (DisruptionRebookPlanResponse) => void;
  setSelectedSlice: (slice: {
    segments: Array<FlightItinerarySegment>;
  }) => void;
  cancelFlowAction?: () => void;
}

export const DisruptionExerciseRebookOnboardingPage = ({
  disruptionProductName,
  disruptionProductLogo,
  disruptedFlight,
  rebookingLimit,
  contractId,
  bookingReturn = false,
  termsLink,
  displayExternalLinkAsModal,
  delayHours,
  hoursString,
  onFlightSelectContinue,
  onContactSupport,
  handleRefundClick,
  setRebookPlanResponse,
  setSelectedSlice,
  cancelFlowAction,
}: DisruptionExerciseRebookOnboardingPageProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const [openBagsLearnMore, setOpenBagsLearnMore] = useState(false);
  const [openReturnLearnMore, setOpenReturnLearnMore] = useState(false);
  const [rebookPlanError, setRebookPlanError] = useState<boolean>(false);
  const [openMoreHelp, setOpenMoreHelp] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const COMPONENT_KEY =
    CoreDisruptionComponent.DisruptionExerciseNotEligiblePage;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const handlePlanResponse = useCallback(
    (response: RebookPlanSuccess) => {
      setRebookPlanResponse(response);
      // if more than one slice, the user need to select which slice to rebook
      if (response.slices.length > 1) {
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.FlightSelect)
        );
      } else if (response.slices.length === 1) {
        const flightSlice = response.slices[0];
        setSelectedSlice(flightSlice);
        if (flightSlice.segments.length === 1) {
          // if 1 segment (no connections) -> search flights
          const origin = flightSlice.segments[0].origin.locationCode;
          const departure =
            flightSlice.segments[0].updatedDeparture ??
            flightSlice.segments[0].scheduledDeparture;
          const destination = flightSlice.segments[0].destination.locationCode;

          onFlightSelectContinue({
            origin: origin,
            destination: destination,
            departure: departure,
            tripSlice: undefined,
          });

          dispatch(
            setDisruptionExerciseProgress(
              DisruptionExerciseProgress.FlightSearch
            )
          );
        } else if (flightSlice.segments.length > 1) {
          // if more than 1 segment (connections) -> select segment
          dispatch(
            setDisruptionExerciseProgress(
              DisruptionExerciseProgress.SegmentSelect
            )
          );
        } else {
          setOpenMoreHelp(true);
        }
      } else {
        setOpenMoreHelp(true);
      }
    },
    [dispatch, setRebookPlanResponse, setSelectedSlice, onFlightSelectContinue]
  );

  const fetchDisruptionRebookPlan = useCallback(() => {
    if (contractId) {
      setLoading(true);
      const req: DisruptionRefundPlanRequest = {
        contractId,
        itineraryId: disruptedFlight.bookedItinerary.id,
      };

      return dispatch(disruptionRebookPlan(req))
        .then((payload) => {
          const response = unwrapResult(payload);
          setLoading(false);
          if (
            response?.DisruptionRebookPlanResponse ===
            DisruptionRebookPlanResponseEnum.RebookPlanSuccess
          ) {
            handlePlanResponse(response);
          } else {
            setRebookPlanError(true);
            setLoading(false);
          }
        })
        .catch((e) => {
          setRebookPlanError(true);
          setLoading(false);
        });
    }
  }, [contractId, dispatch, disruptedFlight, handlePlanResponse]);

  const handleCta = () => {
    fetchDisruptionRebookPlan();
  };

  const handleGoBack = () => {
    dispatch(
      setDisruptionExerciseProgress(DisruptionExerciseProgress.LandingPage)
    );
  };

  const onCloseBagsLearnMore = useCallback(
    () => setOpenBagsLearnMore(false),
    [setOpenBagsLearnMore]
  );

  const onCloseReturnLearnMore = useCallback(
    () => setOpenReturnLearnMore(false),
    [setOpenReturnLearnMore]
  );

  const onOpenReturnLearnMore = useCallback(
    () => setOpenReturnLearnMore(true),
    [setOpenReturnLearnMore]
  );

  const onOpenBagsLearnMore = useCallback(
    () => setOpenBagsLearnMore(true),
    [setOpenBagsLearnMore]
  );

  const onCloseMoreHelp = useCallback(
    () => setOpenMoreHelp(false),
    [setOpenMoreHelp]
  );

  const bullets = [
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletOne",
        {
          maxPerPax: rebookingLimit,
        }
      ),
      index: 1,
    },
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletTwo"
      ),
      index: 2,
    },
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletThree"
      ),
      learnMore: {
        title: t("core-disruption.disruptionUniversalExercise.learnMore"),
        onClick: onOpenBagsLearnMore,
      },
      index: 3,
    },
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletFourWithRebookLimit",
        {
          rebookLimit: rebookingLimit,
        }
      ),
      learnMore: {
        title: t("core-disruption.disruptionUniversalExercise.learnMore"),
        onClick: onOpenReturnLearnMore,
      },
      index: 4,
    },
  ];

  const bagsLearnMore = (
    <DisruptionExerciseMoreInfo
      open={openBagsLearnMore}
      onClose={onCloseBagsLearnMore}
      type={DisruptionExerciseMoreInfoType.Baggage}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      rebookingLimit={rebookingLimit}
      disruptionProductName={disruptionProductName}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
    ></DisruptionExerciseMoreInfo>
  );

  const returnLearnMore = (
    <DisruptionExerciseMoreInfo
      open={openReturnLearnMore}
      onClose={onCloseReturnLearnMore}
      type={DisruptionExerciseMoreInfoType.Return}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      rebookingLimit={rebookingLimit}
      disruptionProductName={disruptionProductName}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
    ></DisruptionExerciseMoreInfo>
  );

  const moreHelpPopup = (
    <DisruptionExerciseMoreInfo
      open={openMoreHelp}
      onClose={onCloseMoreHelp}
      type={DisruptionExerciseMoreInfoType.MoreHelp}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      rebookingLimit={rebookingLimit}
      disruptionProductName={disruptionProductName}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
    ></DisruptionExerciseMoreInfo>
  );

  useEffect(() => {
    if (bookingReturn) {
      fetchDisruptionRebookPlan();
    }
  }, [fetchDisruptionRebookPlan, bookingReturn]);

  return (
    <DisruptionExercisePage
      className={cn("DisruptionExerciseRebookOnboardingPage")}
      pageWidth={DisruptionExercisePageWidth.Regular}
      title={disruptionProductName}
      disruptionProductLogo={disruptionProductLogo}
      onBack={handleGoBack}
      openErrorModal={rebookPlanError}
      onCloseErrorModal={() => {
        setRebookPlanError(false);
        dispatch(resetDisruptionExerciseState());
      }}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      submitButtonTitle={t(
        "core-disruption.disruptionUniversalExercise.continue"
      )}
      submitButtonAction={handleCta}
      submitButtonDisabled={false}
      isLoading={loading}
      loadingMessage={t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.loadingMessage"
      )}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      onCancelFlow={cancelFlowAction}
      content={
        <DisruptionExercisePageContent
          leftContent={[
            <DisruptionExerciseSummaryCard
              title={t(
                "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.title"
              )}
              description={t(
                "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.subtitle"
              )}
            ></DisruptionExerciseSummaryCard>,
            !isDesktopAndUp ? <hr></hr> : <></>,
            <DisruptionExerciseInformationCard
              onSupportClick={onContactSupport}
              bulletPoints={bullets}
              termsLink={termsLink}
            ></DisruptionExerciseInformationCard>,
            bagsLearnMore,
            returnLearnMore,
            moreHelpPopup,
          ]}
        />
      }
    ></DisruptionExercisePage>
  );
};

export default DisruptionExerciseRebookOnboardingPage;
